// Renderer-neutral geometry derived from the line contract.
//
// The backend owns topology. These functions only decide where those topology keys are drawn.
// No machine/runtime facts are duplicated here.

const FLAT_MARGIN = 70;
const FLAT_CELL_W = 590;
const FLAT_CELL_H = 360;
// Cells sit close: they are adjacent bays on one line, not two departments. The gap only has to
// read as a boundary, and a wide one stretches the cross-cell route into a long empty diagonal.
const FLAT_CELL_GAP = 64;

export const FLAT_CANVAS = {
  width: FLAT_MARGIN * 2 + FLAT_CELL_W * 2 + FLAT_CELL_GAP,
  height: FLAT_MARGIN * 2 + FLAT_CELL_H,
};

/**
 * How much perimeter a handled point is entitled to.
 *
 * A station carries a machine sprite; a post or a conveyor is a small diamond. Spacing them EVENLY
 * gives a lathe no more room than a post, and where two stations are adjacent in the robot sequence
 * — OP40 and OP60 are, with no node between them — their art collides. Weighting the walk pushes the
 * big things apart and lets the small ones sit closer, which is both more legible and more like a
 * real cell, where the machines are what take up the floor.
 */
const PERIMETER_WEIGHT = { station: 2.4, node: 1 };

function pointOnPerimeter(index, count, x, y, width, height) {
  if (count <= 1) return { x: x + width / 2, y: y + height / 2 };
  const perimeter = 2 * (width + height);
  let d = (perimeter * index) / count;

  if (d <= width) return { x: x + d, y };
  d -= width;
  if (d <= height) return { x: x + width, y: y + d };
  d -= height;
  if (d <= width) return { x: x + width - d, y: y + height };
  d -= width;
  return { x, y: y + height - d };
}

function ownership(topology) {
  const owner = new Map();
  for (const station of topology?.stations || []) owner.set(station.key, station.cellId);
  for (const node of topology?.nodes || []) owner.set(node.key, node.cellId);
  return owner;
}

export function buildFlatLineLayout(topology) {
  const owner = ownership(topology);
  const points = {};
  const cells = {};
  const robots = {};

  (topology?.cells || []).forEach((cell, cellIndex) => {
    const x = FLAT_MARGIN + cellIndex * (FLAT_CELL_W + FLAT_CELL_GAP);
    const y = FLAT_MARGIN;
    cells[cell.id] = { x, y, width: FLAT_CELL_W, height: FLAT_CELL_H };

    // Shared hand-off nodes appear in both robot sequences. Their owner cell decides where they
    // physically live so both renderers resolve each topology key to one position.
    const ownedSequence = (cell.robotSequence || []).filter((key) => owner.get(key) === cell.id);
    // Walk the perimeter by WEIGHT rather than by count, so a machine gets more floor than a post.
    const isStation = (key) => Boolean((topology?.stations || []).find((st) => st.key === key));
    const weights = ownedSequence.map((key) => (isStation(key) ? PERIMETER_WEIGHT.station : PERIMETER_WEIGHT.node));
    const totalWeight = weights.reduce((a, w) => a + w, 0) || 1;
    let walked = 0;
    ownedSequence.forEach((key, i) => {
      // Place at the CENTRE of this point's share, so neighbours are separated by half of each
      // share rather than by nothing.
      const at = (walked + weights[i] / 2) / totalWeight;
      walked += weights[i];
      points[key] = pointOnPerimeter(
        at * ownedSequence.length,
        ownedSequence.length,
        x + 54,
        y + 52,
        FLAT_CELL_W - 108,
        FLAT_CELL_H - 104,
      );
    });

    robots[cell.robotId] = {
      x: x + FLAT_CELL_W / 2,
      y: y + FLAT_CELL_H / 2,
      cellId: cell.id,
    };
  });

  // Defensive fallback for a future topology key that is not yet assigned to a cell.
  const allKeys = [
    ...(topology?.stations || []).map((s) => s.key),
    ...(topology?.nodes || []).map((n) => n.key),
  ];
  let fallback = 0;
  for (const key of allKeys) {
    if (points[key]) continue;
    points[key] = {
      x: FLAT_MARGIN + 80 + fallback * 80,
      y: FLAT_CANVAS.height - 45,
    };
    fallback += 1;
  }

  return { ...FLAT_CANVAS, points, cells, robots };
}

// How many isometric tiles the whole plan spans.
//
// This is a SPACING knob, not just a scale one. Sprites are drawn at a fixed pixel width, so
// spreading the plan over more tiles widens the gap between neighbours without changing how big a
// machine is — and the viewport's fit-to-content absorbs the extra extent. Raised from 26 once the
// real machine art landed: at 26 the plan was spaced for the old 118px stand-in boxes and OP40's and
// OP60's sprites overlapped, which reads as two machines occupying one floor tile.
//
// It does pull the stations slightly further apart than the robot sequence strictly implies. That is
// an accepted trade: the arrangement is already `layoutSource: "derived"` and labelled illustrative,
// so legibility wins over a fidelity we do not actually have. Real surveyed coordinates, when they
// arrive, land in `station.layout` and override all of this. Kept modest because the viewport fits
// the whole plan, so spreading further only shrinks every machine on screen.
const ISO_TILES_ACROSS = 30;

/**
 * The isometric layout is the FLAT layout, projected.
 *
 * It used to be computed independently — its own perimeter walk on its own tile rectangle — which
 * meant the two views could disagree about where a station sits relative to its neighbours, and they
 * did: OP30 did not sit across from OP20 the way the flat map showed. Two arrangements of the same
 * line is one arrangement too many.
 *
 * So there is now exactly one plan, `buildFlatLineLayout`, and the isometric renders it from a
 * corner: flat pixel coordinates scale uniformly into tile coordinates, and the renderer's own
 * `isoToScreen` turns the plan's rectangles into diamonds. Uniform scale on both axes, so nothing is
 * skewed. Move a station in the flat map and it moves in the isometric, by construction.
 */
export function buildIsoLineLayout(topology) {
  const flat = buildFlatLineLayout(topology);
  const scale = ISO_TILES_ACROSS / Math.max(1, flat.width);
  const toTile = (p) => ({ tx: p.x * scale, ty: p.y * scale });

  const points = {};
  for (const [key, p] of Object.entries(flat.points)) points[key] = toTile(p);

  const cells = {};
  for (const [id, c] of Object.entries(flat.cells)) {
    cells[id] = { tx: c.x * scale, ty: c.y * scale, width: c.width * scale, height: c.height * scale };
  }

  const robots = {};
  for (const [id, r] of Object.entries(flat.robots)) {
    robots[id] = { ...toTile(r), cellId: r.cellId };
  }

  return { points, cells, robots };
}
